/* jshint maxcomplexity: 5, maxparams: 3, maxdepth: 2, maxstatements: 10 */
var searchPartMixin = require('../modules/mixins/searchpart.mixin');
var trackingUtils = require('../modules/utils/tracking.utils.js');
var cons;
var validator;
const parseAge = (ageStr) => {
    const age = parseInt(ageStr, 10);
    return isNaN(age) ? null : age;
};

// Strategy pattern for different passenger selector versions
var passengerStrategies = {
    // Standard strategy (flights/default)
    standard: {
        getPassengers: function(model) {
            return {
                seniors: parseInt(model.get('numSeniors') || model.defaults.numSeniors, 10),
                adults: parseInt(model.get('numAdults') || model.defaults.numAdults, 10),
                children: parseInt(model.get('numChildren') || model.defaults.numChildren, 10),
                infants: parseInt(model.get('numInfants') || model.defaults.numInfants, 10),
            };
        },

        getLimits: function(model, limits) {
            var passengers = this.getPassengers(model);
            var adults = passengers.adults;
            var children = passengers.children;
            var infants = passengers.infants;
            var maxAdults = limits.total - children - infants;
            var maxChildren = Math.min(limits.total - adults - infants,
                adults * limits.childrenPerAdult - infants);
            var maxInfants = Math.min(limits.total - adults - children,
                adults * limits.childrenPerAdult - children,
                adults * limits.infantsPerAdult);
            var minAdults = Math.max(Math.round((children + infants) / limits.childrenPerAdult),
                infants, 1);

            return {
                adults: {
                    min: minAdults,
                    max: maxAdults,
                },
                children: {
                    max: maxChildren,
                },
                infants: {
                    max: maxInfants,
                },
            };
        },

        getSpinners: function() {
            return {
                adults: '.adults_item',
                children: '.children_item',
                infants: '.infants_item',
            };
        },

        getTotalPassengers: function(passengers) {
            return passengers.adults + passengers.children + passengers.infants;
        },

        getTextData: function(passengers, trans) {
            return {
                adults: {
                    num: passengers.adults,
                    text: Odigeo.Utils.pluralizeAll(trans.adults, passengers.adults),
                },
                children: {
                    num: passengers.children,
                    text: Odigeo.Utils.pluralizeAll(trans.children, passengers.children),
                },
                infants: {
                    num: passengers.infants,
                    text: Odigeo.Utils.pluralizeAll(trans.infants, passengers.infants),
                },
            };
        },
    },

    // Train strategy (with different adult types)
    train: {
        getPassengers: function(model) {
            return {
                seniors: parseInt(model.get('numSeniors') ?? model.defaults.numSeniors, 10),
                adults: parseInt(model.get('numAdults') ?? model.defaults.numAdults, 10),
                youth: parseInt(model.get('numYouth') ?? model.defaults.numYouth ?? 0, 10),
                children: 0, // Trains don't have traditional children, youth replaces this
                infants: parseInt(model.get('numInfants') ?? model.defaults.numInfants, 10),
            };
        },

        getLimits: function(model, limits) {
            var passengers = this.getPassengers(model);
            var totalAdultTypes = passengers.seniors + passengers.adults + passengers.youth;
            var infants = passengers.infants;

            var minAdultsPerInfant = Math.max(Math.ceil(infants / (limits.childrenPerAdult || 9)), 0);
            var minTotalAdultTypes = Math.max(1, minAdultsPerInfant);
            var getMinForType = function(currentCount) {
                var othersTotal = totalAdultTypes - currentCount;
                // If others can cover the minimum, this type can go to 0
                if (othersTotal >= minTotalAdultTypes) {
                    return 0;
                }
                // Otherwise, need to keep enough to reach minimum
                return minTotalAdultTypes - othersTotal;
            };

            return {
                seniors: {
                    min: getMinForType(passengers.seniors),
                    max: Math.min(limits.total - passengers.adults - passengers.youth - infants, 9),
                },
                adults: {
                    min: getMinForType(passengers.adults),
                    max: Math.min(limits.total - passengers.seniors - passengers.youth - infants, 9),
                },
                youth: {
                    min: getMinForType(passengers.youth),
                    max: Math.min(limits.total - passengers.seniors - passengers.adults - infants, 9),
                },
                infants: {
                    min: 0,
                    max: Math.min(
                        totalAdultTypes * (limits.childrenPerAdult || 9),
                        limits.total - totalAdultTypes
                    ),
                },
            };
        },

        getSpinners: function() {
            return {
                seniors: '.seniors_item',
                adults: '.adults_item',
                youth: '.children_item', // youth uses children_item class in template
                infants: '.infants_item',
            };
        },

        getTotalPassengers: function(passengers) {
            return passengers.seniors + passengers.adults + passengers.youth + passengers.children + passengers.infants;
        },

        getTextData: function(passengers, trans) {
            return {
                seniors: {
                    num: passengers.seniors,
                    text: Odigeo.Utils.pluralizeAll(trans.seniors || trans.adults, passengers.seniors),
                },
                adults: {
                    num: passengers.adults,
                    text: Odigeo.Utils.pluralizeAll(trans.adults, passengers.adults),
                },
                youth: {
                    num: passengers.youth,
                    text: Odigeo.Utils.pluralizeAll(trans.youth || trans.children, passengers.youth),
                },
                children: {
                    num: passengers.children,
                    text: Odigeo.Utils.pluralizeAll(trans.children, passengers.children),
                },
                infants: {
                    num: passengers.infants,
                    text: Odigeo.Utils.pluralizeAll(trans.infants, passengers.infants),
                },
            };
        },
    },
};

Odigeo.defineWidget('passengers_search_manager', Odigeo.Widget.extend({
    modelClass: Odigeo.Model.extend(searchPartMixin.extend({
        defaults: {
            numSeniors: 0,
            numAdults: 1,
            numYouth: 0,
            numChildren: 0,
            numInfants: 0,
            kid0Age: '',
            kid1Age: '',
            kid2Age: '',
            kid3Age: '',
            kid4Age: '',
            kid5Age: '',
            infant0Age: '',
            infant1Age: '',
            infant2Age: '',
            infant3Age: '',
            youth0Age: '',
            youth1Age: '',
            youth2Age: '',
            youth3Age: '',
            youth4Age: '',
            youth5Age: '',
            youth6Age: '',
            youth7Age: '',
            youth8Age: '',
            infantTrains0Age: '',
            infantTrains1Age: '',
            infantTrains2Age: '',
            infantTrains3Age: '',
            infantTrains4Age: '',
            infantTrains5Age: '',
        },

        init: function() {
            this.options = Odigeo.parseOptions(this.widget.options);
            this._initStrategy();
            return searchPartMixin.getMixin().init.apply(this, arguments);
        },

        _initStrategy: function() {
            // Determine which strategy to use based on search type
            const urlParams = window.location.hash.split(';');
            const searchData = JSON.parse(Odigeo.Session.get('searchParameters')) || {};
            const isTrainsSearchInURL = urlParams.some((params) => {
                const param = params.split('=');
                return param.length == 2 && param[0] === 'trainSearch' && param[1] === 'true';
            });
            const isTrainSearchInSearchData = searchData.trainSearch === true;
            this.strategyType = Odigeo.SessionScope.trainAgeEnabled &&
                (isTrainsSearchInURL || isTrainSearchInSearchData) ? 'train' : 'standard';
            this.strategy = passengerStrategies[this.strategyType];
        },

        getNumSeniors: function() {
            return parseInt(this.get('numSeniors') || this.defaults.numSeniors, 10);
        },

        getNumAdults: function() {
            return parseInt(this.get('numAdults') || this.defaults.numAdults, 10);
        },

        getNumYouth: function() {
            return parseInt(this.get('numYouth') || this.defaults.numYouth, 10);
        },

        getNumChildren: function() {
            return parseInt(this.get('numChildren') || this.defaults.numChildren, 10);
        },

        getNumInfants: function() {
            return parseInt(this.get('numInfants') || this.defaults.numInfants, 10);
        },

        getNumTrainsInfants: function() {
            const infantTrainAges = [...Array(this.getNumInfants())]
                .map((_, i) => parseAge(this.attributes[`infantTrains${i}Age`]))
                .filter((age) => age != null)
                .filter((age) => age <= 3);
            return infantTrainAges.length;
        },

        getNumTrainsChildren: function() {
            const childrenTrainAges = [...Array(this.getNumInfants())]
                .map((_, i) => parseAge(this.attributes[`infantTrains${i}Age`]))
                .filter((age) => age != null)
                .filter((age) => age >= 4 && age <= 11);
            return childrenTrainAges.length;
        },

        getPassengers: function() {
            return this.strategy.getPassengers(this);
        },

        setPaxByType: function(paxType, val) {
            var validTypes = Odigeo._.values(cons.PAX_TYPES);

            if (this.strategyType === 'train') {
                validTypes = validTypes.concat(['youth', 'seniors']);
            }

            if (Odigeo._.includes(validTypes, paxType)) {
                var paxTypeAttributeName = 'num' + Odigeo.Utils.capitalize(paxType);
                this.save(paxTypeAttributeName.toString(), val);
            } else {
                this.trigger('error:paxType', paxType);
            }
        },

        parse: function(searchParams) {
            // Take the trainSearch param straight from the URL because searchParameters
            // in session storage may not be the latest data
            const urlParams = window.location.hash.split(';');
            const isTrainsSearchInURL = urlParams.some((params) => {
                const param = params.split('=');
                return param.length == 2 && param[0] === 'trainSearch' && param[1] === 'true';
            });
            const searchData = JSON.parse(Odigeo.Session.get('searchParameters')) || {};
            const isTrainSearchInSearchData = searchData.trainSearch === true;
            // Check if we're in train mode with age-based categorization
            const isTrainAgeFlow = Odigeo.SessionScope.trainAgeEnabled &&
                (isTrainsSearchInURL || isTrainSearchInSearchData);
            if (isTrainAgeFlow && (searchParams.adultAges || searchParams.childrenAges || searchParams.infantAges)) {
                return this.parseAgeBasedParams(searchParams);
            }

            var defaultParams = {
                numAdults: searchParams.numAdults,
                numChildren: searchParams.numChildren,
                numInfants: searchParams.numInfants,
            };
            var ageParams = Odigeo.AB.isOfferChildrenAgeDesktop ? this.getAgeParamsFromSearch(searchParams) : {};
            return Odigeo._.extend(defaultParams, ageParams);
        },

        parseAgeBasedParams: function(searchParams) {
            var categorizedPassengers = this.categorizePassengersByAge(searchParams);
            var result = {
                numSeniors: categorizedPassengers.seniors.length,
                numAdults: categorizedPassengers.adults.length,
                numYouth: categorizedPassengers.youth.length,
                numInfants: categorizedPassengers.infants.length,
            };

            // Store individual ages for each category
            var ageParams = {};
            Odigeo._.each(categorizedPassengers.seniors, function(age, index) {
                ageParams['senior' + index + 'Age'] = age;
            });
            Odigeo._.each(categorizedPassengers.adults, function(age, index) {
                ageParams['adult' + index + 'Age'] = age;
            });
            Odigeo._.each(categorizedPassengers.youth, function(age, index) {
                ageParams['youth' + index + 'Age'] = age;
            });
            Odigeo._.each(categorizedPassengers.infants, function(age, index) {
                ageParams['infantTrains' + index + 'Age'] = age;
            });

            return Odigeo._.extend(result, ageParams);
        },

        categorizePassengersByAge: function(searchParams) {
            var self = this;
            var categorized = {
                seniors: [],
                adults: [],
                youth: [],
                infants: [],
            };

            // Parse and categorize adult ages
            if (searchParams.adultAges) {
                var adultAges = self.parseAgeString(searchParams.adultAges);
                Odigeo._.each(adultAges, function(age) {
                    if (age >= 60) {
                        categorized.seniors.push(age);
                    } else if (age >= 30 && age < 60) {
                        categorized.adults.push(age);
                    } else if (age >= 12 && age < 30) {
                        categorized.youth.push(age);
                    }
                });
            }

            // Parse and categorize children ages
            if (searchParams.childrenAges) {
                var childrenAges = self.parseAgeString(searchParams.childrenAges);
                Odigeo._.each(childrenAges, function(age) {
                    if (age >= 4 && age <= 11) {
                        categorized.infants.push(age);
                    }
                });
            }

            // Parse infant ages
            if (searchParams.infantAges) {
                var infantAges = self.parseAgeString(searchParams.infantAges);
                Odigeo._.each(infantAges, function(age) {
                    categorized.infants.push(age);
                });
            }

            return categorized;
        },

        parseAgeString: function(ageString) {
            if (!ageString || typeof ageString !== 'string') {
                return [];
            }
            return Odigeo._.map(ageString.split('-'), function(age) {
                return parseInt(age, 10);
            }).filter(function(age) {
                return !isNaN(age) && age >= 0;
            });
        },

        getAgeParamsFromSearch: function(searchParams) {
            var infantAges = this.getAgeParams(searchParams, searchParams.numInfants, 'infant');
            var kidAges = this.getAgeParams(searchParams, searchParams.numChildren, 'kid');
            return Odigeo._.extend(kidAges, infantAges);
        },
        getAgeParams: function(searchParams, numPeople, elementType) {
            return Odigeo._.reduce(Odigeo._.range(numPeople), function(result, i) {
                result[elementType + i + 'Age'] = searchParams[elementType + i + 'Age'];
                return result;
            }, {});
        },
        getLimits: function() {
            var limits = this.widget.options.limits;
            return this.strategy.getLimits(this, limits);
        },
    })),

    viewClass: Odigeo.View.extend({
        templateSettings: {
            evaluate: /\{\{#([\s\S]+?)\}\}/g,
            interpolate: /\{\{[^#{]([\s\S]+?)[^}]\}\}/g,
            escape: /\{\{\{([\s\S]+?)\}\}\}/g,
        },

        events: {
            'tap .passengers_search_input': 'onTapInInput',
            'tap .passengers_search_append': 'onTapInInput',
            'tap .passengers_search_close_btn': 'onTapInCloseBtn',
            'tap .adult_quick_access': 'onTapQuickPax',
            'tap .od-passengerssearch-age-selector-container': 'onTapAgeSelector',
        },

        selectors: {
            viewTemplate: Odigeo.AB.isCssVariationHeaderDesktop ? '.passengers_search_template_cobalt' : '.passengers_search_template',
            trainTemplate: '.passengers_search_template_train',
            inputTemplate: Odigeo.AB.isCssVariationHeaderDesktop ? '.passengers_search_input_template_cobalt' : '.passengers_search_input_template',
            limitsTemplate: '.passengers_search_limits_template',
            limitsCobaltTemplate: '.passengers_search_limits_cobalt_template',
            limitsErrorTemplate: '.passengers_search_limits_error_template',
            input: '.passengers_search_input',
            passengersWrapper: '.view_container',
        },

        slctrs: {
            spinners: {
                // Dynamic - will be set in init based on strategy
            },
            switchers: {
                kids0Age: '.kids0Age',
                kids1Age: '.kids1Age',
                kids2Age: '.kids2Age',
                kids3Age: '.kids3Age',
                kids4Age: '.kids4Age',
                kids5Age: '.kids5Age',
                infants0Age: '.infants0Age',
                infants1Age: '.infants1Age',
                infants2Age: '.infants2Age',
                infants3Age: '.infants3Age',
                youth0Age: '.youth0Age',
                youth1Age: '.youth1Age',
                youth2Age: '.youth2Age',
                youth3Age: '.youth3Age',
                youth4Age: '.youth4Age',
                youth5Age: '.youth5Age',
                youth6Age: '.youth6Age',
                youth7Age: '.youth7Age',
                youth8Age: '.youth8Age',
                infantTrains0Age: '.infantTrains0Age',
                infantTrains1Age: '.infantTrains1Age',
                infantTrains2Age: '.infantTrains2Age',
                infantTrains3Age: '.infantTrains3Age',
                infantTrains4Age: '.infantTrains4Age',
                infantTrains5Age: '.infantTrains5Age',
            },
            container: '.passengers_search_container',
            dropbdownSelector: '.passengers_search_dropdown',
            msgContainer: '.passengers_search_error_container',
        },

        init: function() {
            this.widget.options = Odigeo.parseOptions(this.widget.options);

            this._initStrategySpinners();

            this.listenTo(this.model, 'change', this.render);
            this.listenTo(Odigeo.Events.fake, 'fake:blur', this.onBlur);
            this.listenTo(this.model, 'error', this.handleErrors);
            this.model.listenSearchModelProperty('focused', Odigeo._.bind(this.onChangeFocusSearch, this));
            this.model.listenSearchModelProperty('type', Odigeo._.bind(this.updateValues, this));
        },

        _initStrategySpinners: function() {
            // Get spinners configuration from the model's strategy
            this.slctrs.spinners = this.model.strategy.getSpinners();
        },

        onChangeFocusSearch: function(model, changed) {
            if (changed !== this.selectors.input) {
                this.toggle(cons.HIDE);
            }
        },

        handleErrors: function(errors) {
            const isPaxError = errors && errors.find((err) => err.key === 'passengers');
            $(this.selectors.input).attr('data-error', isPaxError ? 'invalid' : '');
        },

        initializeSpinners: function() {
            Odigeo._.each(this.slctrs.spinners, function(spinner, key) {
                var $spinner = this.$(spinner);
                this.addSelectorToCache(key, $spinner);
                Odigeo.UIElements.enhanceSpinner($spinner);
                $spinner.on('change', Odigeo._.bind(this.onChangeSpinner, this, key));
            }, this);
            return this;
        },

        initializeSwitchers: function() {
            Odigeo._.each(this.slctrs.switchers, function(switcher, key) {
                var $switcher = this.$(switcher);
                this.addSelectorToCache(key, $switcher);
                Odigeo.UIElements.enhanceSwitcher($switcher);
                $switcher.on('change', Odigeo._.bind(this.onChangeSwitcher, this, key));
            }, this);
            return this;
        },

        configSpinners: function(limits) {
            Odigeo._.each(this.slctrs.spinners, function(selector, key) {
                var minRange = limits[key].min;
                var maxRange = limits[key].max;
                var $spinner = this.$(selector);

                $spinner.trigger('config', {
                    maxRange: maxRange,
                    max: maxRange,
                });
                if (!Odigeo._.isUndefined(minRange)) {
                    $spinner.trigger('config', {
                        minRange: minRange,
                        min: minRange,
                    });
                }
            }, this);
        },

        updateLimits: function() {
            this.configSpinners(this.model.getLimits());
            this.checkMsg(cons.MSG.LIMITS);
            return this;
        },

        renderTemplate: function($tmpl, $wrapperTmpl, data) {
            var tmpl = Odigeo._.template($tmpl.html(), this.templateSettings);
            var dataTmpl = Odigeo._.extend({}, data || this.model.toJSON());

            $wrapperTmpl.html(tmpl(dataTmpl));
            return this;
        },

        isOpen: function() {
            return this.$(this.slctrs.container).hasClass('open');
        },

        render: function() {
            var childAges = {};
            var infantAges = {};
            var youthAges = {};
            var infantTrainsAges = {};
            const isTrainAgeFlow = this.model.strategyType === 'train';

            if (Odigeo.AB.isOfferChildrenAgeDesktop) {
                childAges = this.getAges(this.model.attributes, 'numChildren', 'kid');
                infantAges = this.getAges(this.model.attributes, 'numInfants', 'infant');
            }

            if (isTrainAgeFlow) {
                youthAges = this.getAges(this.model.attributes, 'numYouth', 'youth');
                infantTrainsAges = this.getAges(this.model.attributes, 'numInfants', 'infantTrains');
            }

            this.renderTemplate(
                isTrainAgeFlow ? this.$cachedSelectors.trainTemplate : this.$cachedSelectors.viewTemplate,
                this.$cachedSelectors.passengersWrapper,
                {
                    'passengers': this.getRenderData(),
                    'childAges': childAges,
                    'infantAges': infantAges,
                    'youthAges': youthAges,
                    'infantTrainsAges': infantTrainsAges,
                }
            );
            this.initializeSpinners();
            if (Odigeo.AB.isOfferChildrenAgeDesktop || isTrainAgeFlow) {
                this.initializeSwitchers();
            }

            this.updateLimits();
        },

        toggle: function(hasToOpen) {
            var $container = this.$(this.slctrs.container);

            hasToOpen = Odigeo._.isUndefined(hasToOpen) ? !$container.hasClass('open') : hasToOpen;
            $container.toggleClass('open focus', hasToOpen);

            if (Odigeo.AB.isCssVariationHeaderDesktop) {
                this.$(this.slctrs.dropbdownSelector).toggleClass('open focus', hasToOpen);
            }
            return this;
        },

        checkMsg: function(type) {
            if (!Odigeo._.includes(cons.MSG, type)) {
                return false;
            }

            var $msgContainer = this.$(this.slctrs.msgContainer);
            var key = Odigeo._.find(Odigeo._.keys(validator[type]), function(fnKey) {
                return validator[type][fnKey].call(this.model);
            }, this);
            var hasMsg = !Odigeo._.isUndefined(key);

            const templateSelector = key === 'infantPerAdultTrain' ?
                'limitsErrorTemplate' :
                Odigeo.AB.isCssVariationHeaderDesktop ?
                    'limitsCobaltTemplate' : 'limitsTemplate';
            var tmpl = Odigeo._.template(this.$cachedSelectors[templateSelector].html(), this.templateSettings);

            $msgContainer.toggle(hasMsg);
            $msgContainer.html(tmpl({msg: this.widget.options.translate.limits[key]}));
            return hasMsg;
        },

        onTapInCloseBtn: function(event) {
            if (Odigeo.AB.isCssVariationHeaderDesktop) {
                event.stopImmediatePropagation();
            }

            var isTrains = this.model.strategyType === 'train';
            var hasToClosePassengersModification;

            if (isTrains) {
                const missingYouthAges = !this.allAgesFilled('.youth_age_container', this.model.getNumYouth());
                const missingInfantAges = !this.allAgesFilled('.infant_trains_age_container', this.model.getNumInfants());
                this.$el.find('.passengerssearch_validation_kids_message').toggleClass('odf-hidden', !missingInfantAges);
                this.$el.find('.passengerssearch_validation_youth_message').toggleClass('odf-hidden', !missingYouthAges);
                hasToClosePassengersModification = !missingYouthAges && !missingInfantAges;
            } else {
                hasToClosePassengersModification = Odigeo.AB.isOfferChildrenAgeDesktop
                    ? this.allAgesFilled('.kid_age_container', this.model.getNumChildren())
                        && this.allAgesFilled('.infant_age_container', this.model.getNumInfants())
                    : true;
            }

            if (hasToClosePassengersModification) {
                this.toggle(cons.HIDE);
                if (isTrains) {
                    this.updateTrainsValues();
                    this.checkMsg(cons.MSG.LIMITS);
                }
            }
            return this;
        },

        allAgesFilled: function(containerClass, numPeople) {
            var visibleSelectors = this.$el.find(containerClass).not('.odf-hidden');
            var areSelectorsFilled = Odigeo._.eq(Odigeo._.reduce(visibleSelectors, function(accum, element) {
                var isEmptySelector = $(element).find('.age_selector_visible_value').text() === '';
                if (!isEmptySelector) {
                    accum.push($(element));
                }
                $(element).toggleClass('od-validation-error', isEmptySelector);
                return accum;
            }, []).length, numPeople);

            return areSelectorsFilled;
        },

        onChangeSpinner: function(spinner, ev) {
            this.model.setPaxByType(spinner, parseInt($(ev.target).val(), 10));
            this.$cachedSelectors.input.html(this.getText());

            var isTrains = this.model.strategyType === 'train';
            if (isTrains && (spinner === 'youth' || spinner === 'infants')) {
                this.modifyTrainAgeSelectorVisibility(this.model.getNumYouth(), this.model.getNumInfants());
            }

            return this;
        },

        onChangeSwitcher: function(switcher, ev) {
            var $selector = $(ev.currentTarget);
            var $selectorContainer = $selector.closest('.age_selector_container');
            var selectedOptionAge = $selector.find(':selected').text();
            var containerText = ': ' + selectedOptionAge;
            var attribute = this.getModelAttribute(switcher);

            this.model.set(attribute, selectedOptionAge);
            $selectorContainer.find('.age_selector_visible_value').text(containerText);

            var isTrains = this.model.strategyType === 'train';
            if (isTrains) {
                this.modifyTrainAgeSelectorVisibility(this.model.getNumYouth(), this.model.getNumInfants());
            } else {
                this.modifyKidsAndChildrenAgeSelectorVisibility(this.model.getNumChildren(), this.model.getNumInfants());
            }
        },
        getModelAttribute: function(selector) {
            // Handle specific cases for age attribute names
            // kids0Age -> kid0Age, infants0Age -> infant0Age
            // But keep infantTrains0Age as is, and youth0Age as is
            if (selector.startsWith('kids')) {
                return selector.replace('kids', 'kid');
            } else if (selector.startsWith('infants') && !selector.startsWith('infantTrains')) {
                return selector.replace('infants', 'infant');
            }
            return selector;
        },
        modifyKidsAndChildrenAgeSelectorVisibility: function(numKids, numInfants) {
            this.showAgeContainers('.kid_age_container', numKids, 'kid');
            this.showAgeContainers('.infant_age_container', numInfants, 'infant');
        },
        modifyTrainAgeSelectorVisibility: function(numYouth, numInfants) {
            this.showAgeContainers('.youth_age_container', numYouth, 'youth');
            this.showAgeContainers('.infant_trains_age_container', numInfants, 'infant-trains');
        },
        onTapInInput: function() {
            this.toggle();
            this.model.setSearchModelProperty('focused', this.selectors.input);
            this.model.setSearchModelProperty('editsearch', true);
            trackingUtils.trackEvent('searcher_flights', 'open_change_search_pax');
            return this;
        },
        onTapAgeSelector: function(ev) {
            ev.stopPropagation();
        },
        showAgeContainers: function(containerClass, numPeople, peopleType) {
            Odigeo._.each(this.$el.find(containerClass), function(ageSelector) {
                $(ageSelector).toggleClass('odf-hidden', numPeople < $(ageSelector).attr('data-'+peopleType+'-age'));
            });
        },
        onTapQuickPax: function(ev) {
            var numAdults = $(ev.target).data('quick-adults');
            this.model.save('numAdults', parseInt(numAdults, 10));
            this.model.setSearchModelProperty('editsearch', true);
            trackingUtils.trackEvent('searcher_flights', 'passenger_shortcut_' + numAdults);
            return this;
        },

        updateTrainsValues: function() {
            const infantTrainAges = [...Array(this.model.getNumInfants())]
                .map((_, i) => parseAge(this.model.attributes[`infantTrains${i}Age`]))
                .filter((age) => age != null);
            const youthAges = [...Array(this.model.getNumYouth())]
                .map((_, i) => parseAge(this.model.attributes[`youth${i}Age`]))
                .filter((age) => age != null);
            const childrenAges = infantTrainAges.filter((age) => age >= 4 && age <= 11);
            const infantAges = infantTrainAges.filter((age) => age <= 3);
            const numAdults = this.model.getNumAdults() + this.model.getNumSeniors() + this.model.getNumYouth();
            const adultAges = [
                ...new Array(this.model.getNumAdults()).fill(30),
                ...new Array(this.model.getNumSeniors()).fill(60),
                ...youthAges,
            ];
            // This is in case some infant ages are not selected, so the sum of the lengths
            // is smaller than the infants number
            const defaultInfants = childrenAges.length + infantAges.length < this.model.getNumInfants() ?
                this.model.getNumInfants() :
                infantAges.length;
            this.model.setSearchModelProperty('adultAges', adultAges.join('-'));
            this.model.setSearchModelProperty('childrenAges', childrenAges.join('-'));
            this.model.setSearchModelProperty('infantAges', infantAges.join('-'));
            this.model.setSearchModelProperty('numAdults', numAdults);
            this.model.setSearchModelProperty('numChildren', childrenAges.length);
            this.model.setSearchModelProperty('numInfants', defaultInfants);
            return this;
        },

        onBlur: function(ev) {
            if ($(ev.target).closest(this.$el).length === 0) {
                if (this.model.strategyType === 'train' && this.$(this.slctrs.container).hasClass('open')) {
                    this.updateTrainsValues();
                }
                this.toggle(cons.HIDE);
            }
            return this;
        },

        getRenderData: function() {
            var passengers = this.model.getPassengers();

            return Odigeo._.extend({}, {
                numSeniors: passengers.seniors || 0,
                numAdults: passengers.adults,
                numYouth: passengers.youth || 0,
                numChildren: passengers.children,
                numInfants: passengers.infants,
                inputText: this.getText(),
                limits: this.model.getLimits(),
                openedClass: this.isOpen() ? 'open focus' : '',
                quickPaxSelectedClass: {
                    one: passengers.adults === 1 ? 'active' : '',
                    two: passengers.adults === 2 ? 'active' : '',
                    three: passengers.adults === 3 ? 'active' : '',
                },
            }, this.model.attributes);
        },
        getAges: function(modelAttributes, typeOfKid, key) {
            return Odigeo._.reduce(Odigeo._.range(modelAttributes[typeOfKid]), function(result, i) {
                result[i] = modelAttributes[key + i + 'Age'] !== '' ? ': ' + modelAttributes[key + i + 'Age'] : '';
                return result;
            }, {});
        },

        getText: function() {
            var trans = this.widget.options.translate;
            var passengers = this.model.getPassengers();
            var strategy = this.model.strategy;
            var tmpl = Odigeo._.template(this.$cachedSelectors.inputTemplate.html(), this.templateSettings);
            var textData = strategy.getTextData(passengers, trans);

            return tmpl({
                input: Odigeo._.extend(textData, {
                    total: {
                        num: strategy.getTotalPassengers(passengers),
                    },
                }),
            });
        },

        updateValues: function() {
            var data = Odigeo._.pick(this.model.getOriginalData(), ['numAdults', 'numChildren', 'numInfants']);
            var passengers;
            if (this.model.strategyType === 'train') {
                passengers = {
                    numAdults: this.model.getNumAdults() + this.model.getNumSeniors() + this.model.getNumYouth(),
                    numChildren: this.model.getNumTrainsChildren(),
                    numInfants: this.model.getNumTrainsInfants(),
                };
            } else {
                passengers = Odigeo._.mapKeys(this.model.getPassengers(), function(value, key) {
                    return 'num' + Odigeo._.capitalize(key);
                });
            }
            if (!Odigeo._.isEmpty(data) && !Odigeo._.isEqual(data, passengers)) {
                this.model.save('numAdults', parseInt(data.numAdults, 10));
                this.model.save('numChildren', parseInt(data.numChildren, 10));
                this.model.save('numInfants', parseInt(data.numInfants, 10));
                this.render();
            }
        },
    }),
}));

cons = {
    HIDE: false,
    SHOW: true,
    PAX_TYPES: {
        ADULTS: 'adults',
        CHILDREN: 'children',
        INFANTS: 'infants',
    },
    MSG: {
        LIMITS: 'limits',
    },
};

validator = {
    limits: {
        infantPerAdultTrain: function() {
            if (this.strategyType !== 'train') {
                return false;
            }
            var trainChildren = this.getNumTrainsInfants();
            var passengers = this.getPassengers();
            var adultType = passengers.adults + passengers.seniors + passengers.youth;
            return trainChildren / adultType > this.widget.options.limits.infantsPerAdult;
        },

        total: function() {
            var passengers = this.getPassengers();
            var strategy = this.strategy;
            var total = strategy.getTotalPassengers(passengers);
            return total === this.widget.options.limits.total;
        },

        childrenPerAdult: function() {
            if (this.strategyType === 'train') {
                return false;
            }
            var passengers = this.getPassengers();
            return (passengers.children + passengers.infants) / passengers.adults === this.widget.options.limits.childrenPerAdult;
        },

        childrenPerAdultTrain: function() {
            if (this.strategyType !== 'train') {
                return false;
            }
            var trainChildren = this.getNumTrainsChildren();
            var trainInfants = this.getNumTrainsInfants();
            var passengers = this.getPassengers();
            var adultType = passengers.adults + passengers.seniors + passengers.youth;
            return (trainChildren + trainInfants) / adultType === this.widget.options.limits.childrenPerAdult;
        },
    },
};
